import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

const events = [
  {
    label: 'NIKKAH',
    dateDay: '06',
    dateMonth: 'June',
    dateYear: '2026',
    time: '11:30 AM',
    venue: 'St. George Hebron Church Parish Hall, Nagancheri, Kothamangalam',
    mapUrl: 'https://maps.app.goo.gl/FKcGxwNCGhdkv5MG6',
  },
  {
    label: 'RECEPTION',
    dateDay: '07',
    dateMonth: 'June',
    dateYear: '2026',
    time: '6:00 PM – 9:00 PM',
    venue: 'Mela Auditorium, Muvattupuzha',
    mapUrl: 'https://maps.google.com/?q=Mela+Auditorium+Muvattupuzha',
  },
];

function EventCard({
  event,
  index,
}: {
  event: (typeof events)[0];
  index: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 60, filter: 'blur(6px)' }}
      whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.9, delay: index * 0.3 }}
      whileHover={{ y: -8 }}
      className="group bg-ink-light/40 rounded-2xl overflow-hidden border border-copper/8 backdrop-blur-sm transition-all duration-500 hover:border-copper/20 hover:shadow-[0_24px_64px_rgba(0,0,0,0.4)]"
    >
      {/* Top accent bar */}
      <div className="h-[2px] bg-gradient-to-r from-transparent via-copper/50 to-transparent group-hover:via-copper/70 transition-all" />

      <div className="p-8 md:p-10">
        {/* Event badge */}
        <span className="inline-block px-4 py-1.5 rounded-full bg-copper/15 text-copper-light font-sans text-[11px] uppercase font-bold tracking-wider border border-copper/15">
          {event.label}
        </span>

        {/* Date */}
        <div className="flex items-baseline gap-3 mt-8 mb-6">
          <span className="font-display text-6xl md:text-7xl font-bold text-copper-light">
            {event.dateDay}
          </span>
          <div className="flex flex-col">
            <span className="font-sans text-lg uppercase tracking-wider text-cream font-medium">
              {event.dateMonth}
            </span>
            <span className="font-sans text-base text-pearl/50">{event.dateYear}</span>
          </div>
        </div>

        {/* Time */}
        <div className="flex items-center gap-3 mb-5">
          <div className="w-8 h-8 rounded-full bg-copper/10 flex items-center justify-center">
            <i className="ri-time-line text-sm text-copper w-4 h-4 flex items-center justify-center" />
          </div>
          <span className="font-sans text-base text-cream/80 font-medium">{event.time}</span>
        </div>

        {/* Venue */}
        <div className="flex items-start gap-3 mb-8">
          <div className="w-8 h-8 rounded-full bg-copper/10 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-map-pin-2-line text-sm text-copper w-4 h-4 flex items-center justify-center" />
          </div>
          <span className="font-sans text-base text-pearl leading-relaxed">
            {event.venue}
          </span>
        </div>

        {/* View location button */}
        <a
          href={event.mapUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl border border-copper/25 text-cream font-sans text-sm uppercase tracking-wider font-medium transition-all duration-300 hover:bg-copper/15 hover:border-copper/40"
        >
          <i className="ri-map-2-line w-4 h-4 flex items-center justify-center" />
          View Location
          <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
        </a>
      </div>
    </motion.div>
  );
}

export default function EventsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id="events" className="relative py-24 md:py-36 bg-midnight-dark overflow-hidden">
      {/* Background glow */}
      <div className="absolute top-1/3 right-0 w-[400px] h-[400px] rounded-full bg-copper/[0.02] blur-[120px] pointer-events-none" />

      <div className="max-w-5xl mx-auto px-6" ref={ref}>
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9 }}
          className="text-center mb-16 md:mb-24"
        >
          <p className="font-sans text-xs uppercase tracking-[3px] text-copper/60 mb-3">
            Celebration
          </p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-cream font-semibold">
            Celebrate With Us
          </h2>
          <div className="flex items-center justify-center gap-4 mt-6">
            <span className="w-16 md:w-24 h-px bg-copper/20" />
            <div className="w-2 h-2 rotate-45 bg-copper/40" />
            <span className="w-16 md:w-24 h-px bg-copper/20" />
          </div>
        </motion.div>

        {/* Event cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {events.map((event, i) => (
            <EventCard key={event.label} event={event} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}